<template>
  <v-container>
    <!-- Filters -->
    <v-row align="center">
      <v-col md="6"
        >Location data of
        <span
          class="text-h6 font-weight-bold"
          v-text="
            `${
              this.selectedUser
                ? ' ' + this.selectedUser?.name
                : '!!! Please select an User !!!'
            }`
          "
        ></span>
      </v-col>
      <v-col md="4">
        <v-menu
          v-model="date_menu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filter.date"
              label="YYYY-MM-DD"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
              @click:clear="filter.date = null"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filter.date"
            type="date"
            @change="(date_menu = false), refreshMap()"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <!-- Vitals -->
    <v-card>
      <v-container>
        <v-row align="center">
          <v-col> <b>Lat-Lng:</b> {{ selectedLatLng }} </v-col>
          <v-col> <b>Timestamp: </b>{{ timestamp }} </v-col>
          <v-col> <b>Mode:</b> {{ mode }} </v-col>
          <v-col> <b>Battery Level:</b> {{ battery }} </v-col>
          <v-col> <b>Distance Travelled:</b> {{ odometer }} meters </v-col>
        </v-row>
      </v-container>
    </v-card>
    <br />
    <!-- No Tracking Data Message -->
    <v-alert
      v-if="!isLoadingTimestamps && selectedUser && noTrackingData"
      type="warning"
      prominent
      class="mb-4"
    >
      <v-row align="center">
        <v-col class="grow">
          <strong>No location tracking data available.</strong><br />
          <span v-if="loginLocation">
            Employee logged in at {{ loginLocation.time }} but location tracking was not enabled.
            Showing login location below.
          </span>
          <span v-else>
            Employee may not have logged in or location tracking is disabled on their device.
          </span>
        </v-col>
      </v-row>
    </v-alert>

    <!-- Map -->
    <v-row>
      <v-col>
        <v-progress-linear v-if="isLoadingTimestamps" indeterminate color="primary" class="mb-2"></v-progress-linear>
        <div ref="mapContainer" style="width: 100%; height: 500px; z-index: 0; position: relative;"></div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";
import L from "leaflet";

export default {
  name: "Timestamps",
  data() {
    return {
      userId: "",
      timestamps: [],
      filter: {
        month_year: "",
        month: "",
        year: "",
        date: "",
      },
      date_menu: false,
      isLoadingTimestamps: false,
      center: [19.047321, 73.069908],
      selectedLatLng: "",
      timestamp: "",
      mode: "",
      battery: "",
      odometer: "",
      noTrackingData: false,
      loginLocation: null,
      map: null,
      markersLayer: null,
      polylineLayer: null,
    };
  },
  props: ["selectedUser"],
  watch: {
    selectedUser: "refreshMap",
  },
  mounted() {
    this.filter.date = moment().format("YYYY-MM-DD");
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    initMap() {
      if (!this.$refs.mapContainer || this.map) return;
      this.map = L.map(this.$refs.mapContainer).setView(this.center, 18);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this.map);
      this.markersLayer = L.layerGroup().addTo(this.map);
    },
    clearMap() {
      if (this.markersLayer) this.markersLayer.clearLayers();
      if (this.polylineLayer) {
        this.map.removeLayer(this.polylineLayer);
        this.polylineLayer = null;
      }
    },
    async refreshMap() {
      this.isLoadingTimestamps = true;
      this.timestamps = [];
      this.noTrackingData = false;
      this.loginLocation = null;
      this.userId = this.selectedUser.id;

      try {
        let timestamps = await axios.get(
          `/timestamps?user_id=${this.userId}&date=${this.filter.date}`
        );
        this.timestamps = timestamps.data.timestamp || [];
        this.isLoadingTimestamps = false;

        // Ensure map is initialized
        if (!this.map) {
          this.$nextTick(() => {
            this.initMap();
            this.drawOnMap();
          });
        } else {
          this.drawOnMap();
        }
      } catch (error) {
        console.error("Error fetching timestamps:", error);
        this.isLoadingTimestamps = false;
        this.noTrackingData = true;
      }
    },
    async drawOnMap() {
      this.clearMap();

      // Check if no tracking data available
      if (!this.timestamps || this.timestamps.length === 0) {
        this.noTrackingData = true;

        // Try to get login location from attendance record
        try {
          let attendanceResponse = await axios.get(
            `/user_attendances?user_id=${this.userId}&date=${this.filter.date}`
          );
          let attendances = attendanceResponse.data.data || [];
          if (attendances.length > 0) {
            let attendance = attendances[0];
            if (attendance.login_lat && attendance.login_lng) {
              this.loginLocation = {
                lat: parseFloat(attendance.login_lat),
                lng: parseFloat(attendance.login_lng),
                time: attendance.login_time || "Unknown time",
              };
              let pos = [this.loginLocation.lat, this.loginLocation.lng];
              this.map.setView(pos, 18);
              let startIcon = L.icon({
                iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
                shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
              });
              L.marker(pos, { icon: startIcon }).addTo(this.markersLayer)
                .bindPopup("<b>Login Location</b><br>Time: " + this.loginLocation.time);
            }
          }
        } catch (attendanceError) {
          console.error("Error fetching attendance:", attendanceError);
        }
        return;
      }

      let pathCoords = [];

      this.timestamps.forEach((timestamp, t) => {
        let lat = timestamp.latitude;
        let lng = timestamp.longitude;
        let coords = [lat, lng];

        let popupContent = `
          <b>Location</b>: ${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}<br>
          <b>Speed</b>: ${timestamp.speed} km/h<br>
          <b>Heading</b>: ${timestamp.heading}<br>
          <b>Battery level</b>: ${timestamp.battery_level}%<br>
          <b>Timestamp</b>: ${timestamp.created_at}<br>
          <b>Odometer</b>: ${parseInt(timestamp.odometer / 1000)}<br>
          <b>Mode</b>: ${timestamp.type}
        `;

        // Start marker
        if (t === 0) {
          let startIcon = L.icon({
            iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
            shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
            iconSize: [25, 41],
            iconAnchor: [12, 41],
          });
          L.marker(coords, { icon: startIcon }).addTo(this.markersLayer)
            .bindPopup("<b>Start</b><br>" + popupContent);
        }
        // End marker
        else if (t === this.timestamps.length - 1) {
          let endIcon = L.icon({
            iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
            shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
            iconSize: [25, 41],
            iconAnchor: [12, 41],
          });
          L.marker(coords, { icon: endIcon }).addTo(this.markersLayer)
            .bindPopup("<b>End</b><br>" + popupContent);
        }
        // Person marker every 25 points
        else if (t % 25 === 0) {
          let circleMarker = L.circleMarker(coords, {
            radius: 5,
            fillColor: "#e53935",
            color: "#b71c1c",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8,
          });
          circleMarker.bindPopup(popupContent);
          circleMarker.on("mouseover", () => {
            this.selectedLatLng = lat + "-" + lng;
            this.timestamp = timestamp.created_at;
            this.mode = timestamp.type;
            this.battery = timestamp.battery_level;
            this.odometer = timestamp.odometer;
            circleMarker.openPopup();
          });
          circleMarker.addTo(this.markersLayer);
        }

        pathCoords.push(coords);
      });

      // Draw polyline
      this.polylineLayer = L.polyline(pathCoords, {
        color: "red",
        weight: 3,
        opacity: 0.4,
      }).addTo(this.map);

      // Fit map to show full route
      if (pathCoords.length > 1) {
        this.map.fitBounds(L.latLngBounds(pathCoords).pad(0.1));
      } else if (pathCoords.length === 1) {
        this.map.setView(pathCoords[0], 18);
      }
    },
  },
};
</script>
